import axios from 'axios';
import { useDispatch } from 'react-redux';
import { setisLogging } from '../store/authSlice';
import { useCallback, useEffect, useState } from 'react';
import {useLogs} from '../hooks';

const TOKEN_SYNC_TS_KEY = 'tokenUpdatedAt';
const TOKEN_GRACE_PERIOD_MS = 15000;

const isLocalDevHost = () =>
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';

const safeParse = (value) => {
    if (!value) return null;
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
};

const getTokenFromPath = (pathname = window.location.pathname) => {
    const pathArray = pathname.split('/').filter(Boolean);
    const tokenIndex = pathArray.indexOf('token');
    if (tokenIndex === -1 || !pathArray[tokenIndex + 1]) {
        return null;
    }
    return decodeURIComponent(pathArray[tokenIndex + 1]);
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const withGuard = (Component) => {
    
    const Wrapper = (props) =>{
        const dispatch = useDispatch();
        const pathname = window.location.pathname;
        const incomingPathToken = getTokenFromPath(pathname);
        const initialToken = incomingPathToken || localStorage.getItem('token');
        const initialUser = safeParse(localStorage.getItem('user'));
        const {resultLogsPost,postDataHandler} = useLogs()
        const [hadTokenInitially] = useState(() => Boolean(initialToken));
        const [token, setToken] = useState(initialToken);
        const [isTokenExists, setIsTokenExists] = useState(Boolean(initialToken));
        const [isLogin,setIsLogin] = useState(Boolean(initialToken && initialUser));
        const [isLoadingUser, setIsLoadingUser] = useState(true);

        const syncToken = useCallback((nextToken) => {
            if (nextToken) {
                localStorage.setItem('token', nextToken);
                localStorage.setItem(TOKEN_SYNC_TS_KEY, Date.now().toString());
                setToken(nextToken);
                setIsTokenExists(true);
                return;
            }
            localStorage.removeItem('token');
            localStorage.removeItem(TOKEN_SYNC_TS_KEY);
            setToken(null);
            setIsTokenExists(false);
        }, []);

        const redirectToLogin = useCallback(() => {
            if (!isLocalDevHost()) {
                window.location.replace(`${process.env.REACT_APP_Auth_WEB_BASE_URL}`);
            }
        }, []);

        const clearAuthState = useCallback(() => {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('userCurrentTs');
            localStorage.removeItem(TOKEN_SYNC_TS_KEY);
            dispatch(setisLogging({user: null, isLogging: false}));
            setIsLogin(false);
            setIsTokenExists(false);
        }, [dispatch]);

        const fetchCurrentUser = useCallback(async (activeToken) => {
            const maxAttempts = 4;
            for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
                try{
                    const response = await axios.get(
                        `${process.env.REACT_APP_Auth_API_BASE_URL}/users/current`,
                        {
                            headers:{'Authorization': `Bearer ${activeToken}`},
                            validateStatus: () => true
                        }
                    );
                    if (response.status === 200 && response?.data?.data?.user) {
                        return { ok: true, user: response.data.data.user };
                    }
                    if (response.status !== 401 || attempt === maxAttempts - 1) {
                        return { ok: false, status: response.status };
                    }
                } catch (error) {
                    if (attempt === maxAttempts - 1) {
                        return { ok: false, status: error?.response?.status, error };
                    }
                }
                await sleep(300 * (attempt + 1));
            }
            return { ok: false };
        }, []);

        useEffect(() => {
            const latestPathToken = getTokenFromPath(pathname);
            const storedToken = localStorage.getItem('token');
            const preferredToken = latestPathToken || storedToken;

            if (preferredToken !== token) {
                if (latestPathToken && latestPathToken !== storedToken) {
                    localStorage.setItem('token', latestPathToken);
                    localStorage.setItem(TOKEN_SYNC_TS_KEY, Date.now().toString());
                    localStorage.removeItem('user');
                    localStorage.removeItem('userCurrentTs');
                    dispatch(setisLogging({user: null, isLogging: false}));
                    setIsLogin(false);
                }
                setToken(preferredToken || null);
            }

            setIsTokenExists(Boolean(preferredToken));
        }, [dispatch, pathname, token]);

        useEffect(() => {
            const handleStorageChange = () => {
                const latestPathToken = getTokenFromPath();
                const latestStoredToken = latestPathToken || localStorage.getItem('token');
                setToken(latestStoredToken || null);
                setIsTokenExists(Boolean(latestStoredToken));
            };

            window.addEventListener('storage', handleStorageChange);
            return () => window.removeEventListener('storage', handleStorageChange);
        }, []);

        useEffect(() => {
            let isActive = true;

            const getUser = async () => {
                const cachedUserTs = parseInt(localStorage.getItem('userCurrentTs') || '0', 10);
                const cachedUserFresh = cachedUserTs && (Date.now() - cachedUserTs) < 5 * 60 * 1000;
                const cachedUser = safeParse(localStorage.getItem('user'));

                if(cachedUser && cachedUserFresh && token) {
                    dispatch(setisLogging({user: cachedUser,isLogging: true}));
                    setIsLogin(true);
                    setIsLoadingUser(false);
                    return;
                }

                if (!token) {
                    if (getTokenFromPath(pathname)) {
                        return;
                    }
                    if (isActive) {
                        setIsLogin(false);
                        setIsLoadingUser(false);
                    }
                    redirectToLogin();
                    return;
                }

                const latestPathToken = getTokenFromPath(pathname);
                if (latestPathToken && latestPathToken !== token) {
                    if (isActive) {
                        syncToken(latestPathToken);
                    }
                    return;
                }

                let result = await fetchCurrentUser(token);
                if (!isActive) return;

                const tokenUpdatedAt = Number(localStorage.getItem(TOKEN_SYNC_TS_KEY) || '0');
                const tokenIsFresh = tokenUpdatedAt && (Date.now() - tokenUpdatedAt) < TOKEN_GRACE_PERIOD_MS;
                if (!result.ok && result.status === 401 && tokenIsFresh) {
                    await sleep(600);
                    if (!isActive) return;
                    result = await fetchCurrentUser(token);
                    if (!isActive) return;
                }

                if (result.ok) {
                    localStorage.setItem('user', JSON.stringify(result.user));
                    localStorage.setItem('userCurrentTs', Date.now().toString());
                    dispatch(setisLogging({user: result.user,isLogging: true}));
                    setIsLogin(true);
                    setIsLoadingUser(false);
                    return;
                }

                const refreshedPathToken = getTokenFromPath(pathname);
                const refreshedStoredToken = refreshedPathToken || localStorage.getItem('token');
                if (refreshedStoredToken && refreshedStoredToken !== token) {
                    syncToken(refreshedStoredToken);
                    return;
                }

                if (!isLocalDevHost()) {
                    clearAuthState();
                    redirectToLogin();
                } else {
                    dispatch(setisLogging({user: null, isLogging: false}));
                }

                setIsLoadingUser(false);
            };

            setIsLoadingUser(true);
            getUser();

            return () => {
                isActive = false;
            };
        }, [clearAuthState, dispatch, fetchCurrentUser, pathname, redirectToLogin, syncToken, token]);

        useEffect(() => {
            if(!isLogin) return;
            if(hadTokenInitially) return;
            if(!isTokenExists) return;
            postDataHandler({
                moduleName: "SALES",
                resourceName: "SALES",
                eventName:"LOGIN",
                logContentEn: `Login user`,
                logContentAr: `تسجيل دخول`,
            });
        }, [hadTokenInitially, isLogin, isTokenExists, postDataHandler]);

        useEffect(()=>{
            if(resultLogsPost && !hadTokenInitially){
                setIsTokenExists(true);
            }
        },[resultLogsPost, hadTokenInitially]);

        if(isLogin && isTokenExists){
            return <Component {...props} /> 
        }

        if (isLocalDevHost()) {
            return <Component {...props} />;
        }

        if (isLoadingUser) {
            return (
                <div className="flex items-center justify-center h-screen">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">جاري التحقق من بيانات الدخول...</p>
                    </div>
                </div>
            );
        }

        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-center">
                    <p className="text-gray-600">جاري التحويل...</p>
                </div>
            </div>
        );
    }
    return Wrapper;
}

export default withGuard
